import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { updateWorkedHours } from '../services/workedHours.services';
import { getEmployeeAssignments } from '@/pages/shiftManagement/services/scheduleManagement.services';

const AuthorizationDialog = ({ isOpen, onClose, record, onSuccess }) => {
  const [totalHours, setTotalHours] = useState(0);
  const [hasLunch, setHasLunch] = useState(false);
  const [lunchMinutes, setLunchMinutes] = useState(60);
  const [authIngreso, setAuthIngreso] = useState('');
  const [authSalida, setAuthSalida] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [plannedTimes, setPlannedTimes] = useState({ start: null, end: null, lunch: null });
  const [loadingPlanned, setLoadingPlanned] = useState(false);
  const { toast } = useToast();

  const normalizeTimeHHMM = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return '';
    const [h, m] = hhmm.split(':');
    const hh = String(parseInt(h || '0', 10)).padStart(2, '0');
    const mm = String(parseInt(m || '0', 10)).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const formatToAmPm = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return '';
    const [h, m] = hhmm.split(':').map((n) => parseInt(n || '0', 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return '';
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    const hh12 = String(hour12).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    return `${hh12}:${mm} ${period}`;
  };

  const timeToMinutes = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string') return null;
    const [hh, mm] = hhmm.split(':').map((n) => parseInt(n, 10));
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    return hh * 60 + mm;
  };

  const computeTotal = (ingreso, salida, lunch, lunchMin) => {
    const mi = timeToMinutes(ingreso);
    const ms = timeToMinutes(salida);
    if (mi == null || ms == null) return 0;
    let diff = ms - mi;
    if (diff < 0) diff += 24 * 60; // cruza medianoche
    if (lunch) diff = Math.max(0, diff - (Number(lunchMin) || 0));
    // mantener 2 decimales sin redondeos agresivos
    return Math.round((diff / 60) * 100) / 100;
  };

  const DAY_NAMES = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];

  // Calcular weekStart (lunes) a partir de una fecha
  const getWeekStart = (dateStr) => {
    const d = new Date(`${dateStr}T12:00:00`);
    const day = d.getDay(); // 0=dom, 1=lun, ...
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return d.toISOString().split('T')[0];
  };

  // Obtener horarios planificados del empleado desde WeeklyAssignment
  const fetchPlannedTimes = async () => {
    if (!record?.empleado?._id && !record?.empleado?.id) return;
    if (!record?.fecha) return;
    if (!record?.lavanderia?.id) return;

    setLoadingPlanned(true);
    try {
      const employeeId = record.empleado._id || record.empleado.id;
      const laundryCenterId = record.lavanderia.id;

      const recordDate = new Date(record.fecha);
      const year = recordDate.getFullYear();
      const month = String(recordDate.getMonth() + 1).padStart(2, '0');
      const day = String(recordDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const weekStart = getWeekStart(dateStr);
      const dayOfWeek = DAY_NAMES[recordDate.getDay()];

      let assignments = await getEmployeeAssignments(laundryCenterId, weekStart, { employeeId });

      let assignment = Array.isArray(assignments)
        ? assignments.find(a => String(a.employeeId) === String(employeeId))
        : null;

      // Algunos registros de horas trabajadas guardaron el id del empleado
      // vigente en ese momento, que puede diferir del _id actual del empleado
      // (p. ej. si el empleado fue recreado/editado). En ese caso el id ya no
      // coincide con el employeeId de la asignación, aunque el horario sí
      // exista. Reintentamos la búsqueda por cédula como respaldo.
      if (!assignment && record?.empleado?.cedula) {
        assignments = await getEmployeeAssignments(laundryCenterId, weekStart, {
          cedula: record.empleado.cedula,
        });
        assignment = Array.isArray(assignments) ? assignments[0] : null;
      }

      if (assignment?.horarios?.[dayOfWeek]?.length > 0) {
        const ranges = assignment.horarios[dayOfWeek];
        const starts = ranges.map(r => r.inicio).sort();
        const ends = ranges.map(r => r.fin).sort();
        const startTime = starts[0];
        const endTime = ends[ends.length - 1];

        // Calcular total horas planificadas sumando todos los rangos
        let totalMinutes = 0;
        ranges.forEach(r => {
          const [sh, sm] = r.inicio.split(':').map(Number);
          const [eh, em] = r.fin.split(':').map(Number);
          totalMinutes += (eh * 60 + em) - (sh * 60 + sm);
        });
        const totalPlannedHours = Math.round((totalMinutes / 60) * 100) / 100;

        setPlannedTimes({
          start: startTime,
          end: endTime,
          totalHours: totalPlannedHours,
          role: assignment.shiftRole || null,
          scheduleType: assignment.scheduleType || null,
        });
      } else {
        setPlannedTimes({ start: null, end: null, totalHours: null, role: null, scheduleType: null });
      }
    } catch (error) {
      console.error('Error fetching planned times:', error);
      setPlannedTimes({ start: null, end: null, totalHours: null, role: null, scheduleType: null });
    } finally {
      setLoadingPlanned(false);
    }
  };

  useEffect(() => {
    if (record) {
      const lunchTaken = true; // marcado por defecto
      const initialLunchMinutes = record.almuerzoMinutos || 60;
      setHasLunch(lunchTaken);
      setLunchMinutes(initialLunchMinutes);
      // Prefill horas autorizadas con las originales (formato 24h HH:mm)
      const hi = normalizeTimeHHMM(record.horaIngreso || '');
      const hs = normalizeTimeHHMM(record.horaSalida || '');
      setAuthIngreso(hi);
      setAuthSalida(hs);
      const computed = computeTotal(hi, hs, lunchTaken, initialLunchMinutes);
      setTotalHours(computed);

      // Cargar horarios planificados
      fetchPlannedTimes();
    } else {
      setTotalHours(0);
      setHasLunch(false);
      setLunchMinutes(60);
      setAuthIngreso('');
      setAuthSalida('');
      setPlannedTimes({ start: null, end: null, totalHours: null, role: null, scheduleType: null });
    }
  }, [record]);

  const handleLunchChange = (checked) => {
    setHasLunch(checked);
    const computed = computeTotal(authIngreso, authSalida, checked, lunchMinutes);
    setTotalHours(computed);
  };

  const handleLunchMinutesChange = (value) => {
    const minutes = Math.max(0, parseInt(value, 10) || 0);
    setLunchMinutes(minutes);
    const computed = computeTotal(authIngreso, authSalida, hasLunch, minutes);
    setTotalHours(computed);
  };

  const handleAuthorize = async () => {
    if (!record) return;

    setIsLoading(true);
    try {
      const updatedData = {
        totalHorasAutorizadas: totalHours,
        almuerzo: hasLunch,
        almuerzoMinutos: hasLunch ? lunchMinutes : 0,
        horaIngresoAutorizada: authIngreso,
        horaSalidaAutorizada: authSalida,
        horaIngresoPlanificada: plannedTimes.start || null,
        horaSalidaPlanificada: plannedTimes.end || null,
        totalHorasPlanificadas: plannedTimes.totalHours || null,
        isAprobado: true,
      };

      await updateWorkedHours(record._id, updatedData);

      toast({
        title: 'Horas Autorizadas',
        description: `Las horas de ${record?.empleado?.nombre || 'Empleado'} han sido autorizadas con éxito.`,
      });
      onSuccess();
    } catch (error) {
      console.error('Error authorizing hours:', error);
      toast({
        title: 'Error al autorizar',
        description: 'No se pudo actualizar el registro. Inténtelo de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!record) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>Autorizar Horas Trabajadas</DialogTitle>
          <DialogDescription>
            Revisa y ajusta las horas para {record?.empleado?.nombre || 'Empleado'} del día {record?.fecha ? new Date(record.fecha).toLocaleDateString() : ''}.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Fila: Horarios Planificados */}
          <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200">Horarios Planificados</h3>
              {plannedTimes.role && (
                <div className="flex items-center gap-2">
                  {plannedTimes.role.color && (
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: plannedTimes.role.color }} />
                  )}
                  <span className="text-xs font-medium text-blue-700 dark:text-blue-300">{plannedTimes.role.nombre}</span>
                  {plannedTimes.scheduleType?.nombre && (
                    <span className="text-xs text-blue-500 dark:text-blue-400">• {plannedTimes.scheduleType.nombre}</span>
                  )}
                </div>
              )}
            </div>
            {loadingPlanned ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Cargando horarios planificados...</div>
            ) : plannedTimes.start && plannedTimes.end ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-blue-700 dark:text-blue-300">Entrada Planificada</Label>
                  <Input 
                    value={formatToAmPm(plannedTimes.start)} 
                    readOnly 
                    className="bg-white dark:bg-gray-800 border-blue-300 dark:border-blue-600 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="text-blue-700 dark:text-blue-300">Salida Planificada</Label>
                  <Input 
                    value={formatToAmPm(plannedTimes.end)} 
                    readOnly 
                    className="bg-white dark:bg-gray-800 border-blue-300 dark:border-blue-600 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="text-blue-700 dark:text-blue-300">Total Horas Planificadas</Label>
                  <Input 
                    value={plannedTimes.totalHours !== null ? `${plannedTimes.totalHours.toFixed(2)} horas` : 'No calculado'} 
                    readOnly 
                    className="bg-white dark:bg-gray-800 border-blue-300 dark:border-blue-600 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>
            ) : (
              <div className="text-sm text-amber-600 dark:text-amber-400">
                No hay asignación en el nuevo calendario para este día. Debe planificar en Gestión de Turnos.
              </div>
            )}
          </div>

          {/* Fila: originales */}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Horas Trabajadas (Registradas)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Hora de Ingreso</Label>
                <Input 
                  value={formatToAmPm(record?.horaIngreso || '')} 
                  readOnly 
                  className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Hora de Salida</Label>
                <Input 
                  value={formatToAmPm(record?.horaSalida || '')} 
                  readOnly 
                  className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-300">Total Horas Original</Label>
                <Input 
                  value={(record?.totalHorasTrabajadas ?? 0).toFixed(2)} 
                  readOnly 
                  className="bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          </div>
          {/* Fila: autorizadas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Hora de Ingreso (autorizada)</Label>
              <Input
                type="time"
                value={authIngreso}
                onChange={(e) => {
                  const v = normalizeTimeHHMM(e.target.value);
                  setAuthIngreso(v);
                  setTotalHours(computeTotal(v, authSalida, hasLunch, lunchMinutes));
                }}
              />
              <div className="text-xs text-muted-foreground mt-1">{formatToAmPm(authIngreso)}</div>
            </div>
            <div>
              <Label>Hora de Salida (autorizada)</Label>
              <Input
                type="time"
                value={authSalida}
                onChange={(e) => {
                  const v = normalizeTimeHHMM(e.target.value);
                  setAuthSalida(v);
                  setTotalHours(computeTotal(authIngreso, v, hasLunch, lunchMinutes));
                }}
              />
              <div className="text-xs text-muted-foreground mt-1">{formatToAmPm(authSalida)}</div>
            </div>
            <div>
              <Label>Total Horas a Aprobar</Label>
              <Input value={totalHours.toFixed(2)} readOnly />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Checkbox id="lunch" checked={hasLunch} onCheckedChange={handleLunchChange} />
              <Label htmlFor="lunch">¿Tuvo almuerzo? (se restará del total)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Input
                type="number"
                min={0}
                step={5}
                value={lunchMinutes}
                onChange={(e) => handleLunchMinutesChange(e.target.value)}
                disabled={!hasLunch}
                className="w-20"
              />
              <span className="text-sm text-muted-foreground">minutos</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button onClick={handleAuthorize} disabled={isLoading}>
            {isLoading ? 'Autorizando...' : 'Autorizar y Finalizar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AuthorizationDialog;
